import {PropPath} from '@mintdata-wsp/mint-kit/api/properties/PropPath';
import {ANumber} from '@mintdata-wsp/mint-kit/api/values/ANumber';
import {IJsonPatch} from '@mintdata-wsp/thirdparty/jiff/lib/jsonPatch';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DesignEdit} from '@mintdata-wsp/wsp/controllers/multiplayer/MultiplayerController';
import {AString} from '@mintdata-wsp/mint-kit/api/values/AString';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {Type} from '@mintdata-wsp/mint-kit/api/types/Type';
import {IValue} from '@mintdata-wsp/mint-kit/api/types/Value';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';

interface IPropertyPatchTargetData
{
    pageId:string;
    boxId:string;
    propGroup:string;
    propName:string;
    propField:string;
}

interface ISlotPatchTargetData
{
    workbookId:string;
    sheetId:number;
    slotId:string;
}

export class SmartPatchController
{
    public tryToCombineEdits(edits:DesignEdit[])
    {
        if( edits.length < 2)
            return edits;

        for( let i = edits.length - 1; i > 0; i-- )
            for( let k = i - 1; k >= 0; k-- )
                if( this._isSamePatchSet(edits[i].patches, edits[k].patches) )
                    edits[k].patches = [];

        return edits;
    }

    public trySmartPatching(patches:IJsonPatch[]):boolean
    {
        const {openDesign, activeUsersSync, surfaceSelection} = controllers.ui;

        if( !openDesign.getDesignState() || !openDesign.getDesignState().surface )
            return false;

        if( !patches || !patches.length )
            return false;

        for( const patch of patches )
            if( !this._isSmartPatchingApplicable(patch) )
                return false;

        console.debug('smart patching', patches);

        openDesign.stopWatchingDesignState();

        setTimeout(() => activeUsersSync.clearBoxMoveState());

        for( const patch of patches )
            this._applySmartPatching(patch);

        const selectionMode = surfaceSelection.getSelectionInteractionMode();

        if( !surfaceSelection.isSelectionMoving(selectionMode) && !surfaceSelection.isSelectionResizing(selectionMode) )
            setTimeout(() => openDesign.startWatchingDesignState(), 100);

        return true;
    }

    private _isSamePatchSet(patchesA:IJsonPatch[], patchesB:IJsonPatch[]):boolean
    {
        if( patchesA.length !== patchesB.length )
            return false;

        for( let i = 0; i < patchesA.length; i++ )
            if( !this._isSamePatchTarget(patchesA[i], patchesB[i]) )
                return false;

        return true;
    }

    private _isSamePatchTarget(patchA:IJsonPatch, patchB:IJsonPatch):boolean
    {
        return patchA.op === patchB.op && patchA.path === patchB.path;
    }

    private _applySmartPatching(patch:IJsonPatch):void
    {
        const {openDesign} = controllers.ui;

        const designState = openDesign.getDesignState();

        const value = patch.value;

        // component properties

        const componentPropertyRegex = '/surface/pages/(?<pageId>.*)/artboard/allBoxes/(?<boxId>.*)/componentDesc/_(?<propGroup>.*)/(?<propName>.*)/_value/(?<propField>.*)/value';

        const propertyPathMatch = patch.path.match(componentPropertyRegex);

        if( propertyPathMatch )
        {
            // property patches

            const matchGroups:IPropertyPatchTargetData = patch.path.match(componentPropertyRegex)['groups'];
            const { pageId, boxId, propGroup, propName, propField } = matchGroups;

            if( this._isBoxLayoutPatch(patch) )
            {
                const box = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId);

                if( box )
                {
                    const prop = box.componentDesc[propGroup].get(propName);

                    prop.setPropValue(PropPath.fromFieldPath(propField), ANumber.fromNumber(value));
                }
            }

            if( this._isTextValuePatch(patch) )
            {
                const box = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId);

                if( box )
                {
                    const prop = box.componentDesc[propGroup].get(propName);

                    prop.setPropValue(PropPath.fromFieldPath(propField), AString.fromString(value));
                }
            }
        }

        // // box add/remove patches

        // if( this._isBoxArtboardAddRemovePatch(patch) )
        // {
        //     const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/boxes/(?<childBoxIndex>\\d+)$';

        //     const matchGroups = patch.path.match(surfaceRegex)['groups'];
        //     const {pageId, childBoxIndex} = matchGroups;

        //     const {artboard} = designState.surface.pages.get(pageId);

        //     if( patch.op === 'add' )
        //     {
        //         // protection: do not add box, when it is already there
        //         const existingChildIndex = artboard.boxes.findIndex(box => box.id === patch.value);

        //         if(  existingChildIndex !== -1 )
        //         {
        //             console.debug('add box: box exists!');
        //             artboard.boxes.splice(existingChildIndex, 1);
        //         }

        //         console.debug('add box: parent: artboard, child id: ' + patch.value + ', child index: ' + childBoxIndex);
        //         const childBox = designState.surface.pages.get(pageId).artboard.allBoxes.get(patch.value);
        //         designState.surface.pages.get(pageId).artboard.boxes.splice(childBoxIndex, 0, childBox);

        //         console.debug('boxes now', artboard.boxes.map(box => box.id));
        //     }

        //     if( patch.op === 'remove' )
        //     {
        //         if( childBoxIndex < artboard.boxes.length )
        //         {
        //             console.debug('remove box: parent: artboard, child id: ' + artboard.boxes[childBoxIndex].id + ', child index: ' + childBoxIndex);
        //             artboard.boxes.splice(childBoxIndex, 1);
        //         }
        //         else
        //             console.debug('failed to remove box! parent: artboard, children: ' + artboard.boxes + ', child index: ' + childBoxIndex);
        //     }
        // }

        // if( this._isBoxChildrenAddRemovePatch(patch) )
        // {
        //     const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/allBoxes/(?<boxId>.*)/children/(?<childBoxIndex>\\d+)$';

        //     const matchGroups = patch.path.match(surfaceRegex)['groups'];
        //     const {pageId, boxId, childBoxIndex} = matchGroups;

        //     const parentBox = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId);

        //     if( patch.op === 'add' )
        //     {
        //         // protection: do not add box, when it is already there                
        //         const existingChildIndex = parentBox.children.findIndex(box => box.id === patch.value);
                
        //         if( existingChildIndex !== -1 )
        //         {
        //             console.debug('add box: box exists!');
        //             // parentBox.children.splice(existingChildIndex, 1);
        //         }
        //         else
        //         {
        //             console.debug('add box: parent: ' + boxId + ', child id: ' + patch.value + ', child index: ' + childBoxIndex);
        //             const childBox = designState.surface.pages.get(pageId).artboard.allBoxes.get(patch.value);
        //             parentBox.children.splice(childBoxIndex, 0, childBox);
        //         }

        //         console.debug('boxes now', parentBox.children.map(box => box.id));
        //     }

        //     if( patch.op === 'remove' )
        //     {
        //         if( childBoxIndex < parentBox.children.length )
        //         {
        //             console.debug('remove box: parent: ' + boxId + ', child id: ' + parentBox.children[childBoxIndex].id + ', child index: ' + childBoxIndex);
        //             parentBox.children.splice(childBoxIndex, 1);
        //         }
        //         else
        //             console.debug('failed to remove box! parent: ' + boxId + ', children: ' + parentBox.children + ', child index: ' + childBoxIndex);
        //     }
        // }

        // if( this._isBoxChangeParentPatch(patch) )
        // {
        //     const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/allBoxes/(?<boxId>.*)/parent';

        //     const matchGroups = patch.path.match(surfaceRegex)['groups'];
        //     const {pageId, boxId} = matchGroups;

        //     const box = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId);

        //     if( !box )
        //         console.debug('box ' + boxId + ' is not found!');

        //     const parentBox = patch.value === null ? null : designState.surface.pages.get(pageId).artboard.allBoxes.get(patch.value);

        //     box.parent = parentBox;
        //     console.debug('parent of box ' + (box !== null ? box.id : '???') + ' is now: ' + (parentBox !== null ? parentBox.id : 'artboard'));

        // }

        if( patch.path.startsWith('/spreadsheets/workbooks') )
        {
            const spreadsheetRegex = '/spreadsheets/workbooks/(?<workbookId>.*)/runtime/sheets/(?<sheetId>.*)/slots/(?<slotId>.*)/formula.*';

            const matchGroups:ISlotPatchTargetData = patch.path.match(spreadsheetRegex)['groups'];
            const {workbookId, sheetId, slotId} = matchGroups;

            if( this._isSlotFormulaPatch(patch) )
            {
                const sheet = designState.spreadsheets.workbooks.get(workbookId).runtime.sheets[sheetId] as Sheet;

                if( sheet )
                {
                    const slot = sheet.slots.get(slotId) as Slot;
                    slot.setFormula(typeof value === 'string' ? value : Type.deserializeIValue(value));
                }
            }

            if( this._isSlotFormulaValuePatch(patch) )
            {
                const sheet = designState.spreadsheets.workbooks.get(workbookId).runtime.sheets[sheetId] as Sheet;

                if( sheet )
                {
                    const slot = sheet.slots.get(slotId) as Slot;
                    const type = Type.valueCtorFromType((slot.formula as IValue).getType());
                    slot.setFormula(new type(value));
                }
            }
        }
    }

    // private _detectAndApplyReparenting(patches:IJsonPatch[]):IJsonPatch[]
    // {
    //     const {openDesign, surfaceArea} = controllers.ui;

    //     const designState = openDesign.getDesignState();

    //     const reparentingMap:Map<string, {pageId:string, originBoxId:string, targetBoxId:string, prevPosition, nextPosition}> = new Map();

    //     const patchesCopy:IJsonPatch[] = [...patches];

    //     const addRemoveBoxPatches:IJsonPatch[] = [];

    //     // fill in the map with every box whose parent has changed
    //     for( let i = 0; i < patchesCopy.length; i++ )
    //     {
    //         const patch = patchesCopy[i];

    //         if( this._isBoxChangeParentPatch(patch) )
    //         {
    //             const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/allBoxes/(?<boxId>.*)/parent';

    //             const matchGroups = patch.path.match(surfaceRegex)['groups'];
    //             const {pageId, boxId} = matchGroups;

    //             const box = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId);
    //             const originPosition = box.parent.children.findIndex(childBox => childBox.id === box.id);

    //             reparentingMap.set(boxId, {
    //                 pageId,
    //                 originBoxId: box.parent !== null ? box.parent.id : null,
    //                 targetBoxId: patch.value,
    //                 prevPosition: originPosition,
    //                 nextPosition: undefined
    //             });

    //             // patchesCopy.splice(i--, 1);
    //         }
    //     }

    //     // find related changes
    //     for( let i = 0; i < patchesCopy.length; i++ )
    //     {
    //         const patch = patchesCopy[i];
    //         if( this._isBoxChildrenAddRemovePatch(patch) )
    //         {
    //             const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/allBoxes/(?<boxId>.*)/children/(?<childBoxIndex>\\d+)$';

    //             const matchGroups = patch.path.match(surfaceRegex)['groups'];
    //             const {pageId, boxId, childBoxIndex} = matchGroups;

    //             if( patch.op === 'add' )
    //             {
    //                 const reparentingBox = reparentingMap.get(patch.value);

    //                 if( reparentingBox )
    //                 {
    //                     const box = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId);

    //                     // TODO: search patches for remove?
    //                     reparentingBox.targetBoxId = boxId;
    //                     reparentingBox.originBoxId = box.parent ? box.parent.id : null;
    //                     reparentingBox.nextPosition = childBoxIndex;
    //                     // patchesCopy.splice(i--, 1);
    //                 }
    //             }
    //         }

    //         if( this._isBoxArtboardAddRemovePatch(patch) )
    //         {
    //             const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/boxes/(?<childBoxIndex>\\d+)$';

    //             const matchGroups = patch.path.match(surfaceRegex)['groups'];
    //             const {pageId, childBoxIndex} = matchGroups;

    //             if( patch.op === 'add' )
    //             {
    //                 const reparentingBox = reparentingMap.get(patch.value);

    //                 if( reparentingBox )
    //                 {
    //                     reparentingBox.targetBoxId = null;
    //                     reparentingBox.nextPosition = childBoxIndex;
    //                     patchesCopy.splice(i--, 1);
    //                 }
    //             }
    //         }
    //     }

    //     for( let i = 0; i < patchesCopy.length; i++ )
    //     {
    //         const patch = patchesCopy[i];

    //         if( this._isBoxChildrenAddRemovePatch(patch) )
    //         {
    //             const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/allBoxes/(?<boxId>.*)/children/(?<childBoxIndex>\\d+)$';

    //             const matchGroups = patch.path.match(surfaceRegex)['groups'];
    //             const {pageId, boxId, childBoxIndex} = matchGroups;

    //             if( patch.op === 'add' )
    //             {
    //                 const reparentingBox = reparentingMap.get(patch.value);

    //                 if( reparentingBox )
    //                 {
    //                     const box = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId);

    //                     // TODO: search patches for remove?
    //                     reparentingBox.targetBoxId = boxId;
    //                     reparentingBox.originBoxId = box.parent ? box.parent.id : null;
    //                     reparentingBox.nextPosition = childBoxIndex;
    //                     // patchesCopy.splice(i--, 1);
    //                 }
    //             }
                
    //             if( patch.op === 'remove' )
    //             {
    //                 const childBox = designState.surface.pages.get(pageId).artboard.allBoxes.get(boxId).children[childBoxIndex];
    //                 const reparentingBox = reparentingMap.get(childBox.id);

    //                 if( reparentingBox )
    //                 {
    //                     reparentingBox.originBoxId = boxId;
    //                     reparentingBox.prevPosition = childBoxIndex;
    //                     // patchesCopy.splice(i--, 1);
    //                 }
    //             }
    //         }

    //         if( this._isBoxArtboardAddRemovePatch(patch) )
    //         {
    //             const surfaceRegex = '/surface/pages/(?<pageId>.*)/artboard/boxes/(?<childBoxIndex>\\d+)$';

    //             const matchGroups = patch.path.match(surfaceRegex)['groups'];
    //             const {pageId, childBoxIndex} = matchGroups;

    //             if( patch.op === 'add' )
    //             {
    //                 const reparentingBox = reparentingMap.get(patch.value);

    //                 if( reparentingBox )
    //                 {
    //                     reparentingBox.targetBoxId = null;
    //                     reparentingBox.nextPosition = childBoxIndex;
    //                     // patchesCopy.splice(i--, 1);
    //                 }
    //             }

    //             if( patch.op === 'remove' )
    //             {
    //                 const childBox = designState.surface.pages.get(pageId).artboard.boxes[childBoxIndex];
    //                 const reparentingBox = reparentingMap.get(childBox.id);

    //                 if( reparentingBox )
    //                 {
    //                     reparentingBox.originBoxId = null;
    //                     reparentingBox.prevPosition = childBoxIndex;
    //                     // patchesCopy.splice(i--, 1);
    //                 }
    //             }
    //         }
    //     }

    //     // apply if any
    //     reparentingMap.forEach((boxData, boxId) =>
    //     {
    //         const childBox = designState.surface.pages.get(boxData.pageId).artboard.allBoxes.get(boxId);
            
    //         const originBox = boxData.originBoxId === null
    //             ? null
    //             : designState.surface.pages.get(boxData.pageId).artboard.allBoxes.get(boxData.originBoxId);

    //         const targetBox = boxData.targetBoxId === null
    //             ? null
    //             : designState.surface.pages.get(boxData.pageId).artboard.allBoxes.get(boxData.targetBoxId);
        
    //         // surfaceArea.moveIntoBox(childBox, targetBox, targetBox ? targetBox.hostId : null);
    //         if( boxData.targetBoxId && !targetBox)
    //             return;  // TODO: cases
            
    //         if( boxData.originBoxId && !originBox)
    //             return;  // TODO: cases

    //         childBox.parent = targetBox;

    //         if( targetBox )
    //             targetBox.children.splice(boxData.nextPosition, 0, childBox);
    //         else
    //             // TODO: check duplicates
    //             designState.surface.pages.get(boxData.pageId).artboard.boxes.splice(boxData.nextPosition, 0, childBox);

    //         if( originBox )
    //             originBox.children.splice(boxData.prevPosition, 1);
    //         else
    //             // TODO: remove by id
    //             designState.surface.pages.get(boxData.pageId).artboard.boxes.splice(boxData.prevPosition, 1);
    //     });

    //     return patchesCopy;
    // }

    private _isSmartPatchingApplicable(patch:IJsonPatch):boolean
    {
        const designState = controllers.ui.openDesign.getDesignState();

        if( !designState )
            return false;

        return this._isBoxLayoutPatch(patch)
            // || this._isBoxArtboardAddRemovePatch(patch)
            // || this._isBoxChildrenAddRemovePatch(patch)
            // || this._isBoxChangeParentPatch(patch)
            || this._isTextValuePatch(patch)
            || this._isSlotFormulaPatch(patch)
            || this._isSlotFormulaValuePatch(patch);
    }

    private _isBoxLayoutPatch(patch:IJsonPatch):boolean
    {
        const pathRegex = '/surface/pages/(.*)/artboard/allBoxes/(.*)/componentDesc/_layoutProperties/(.*)/_value/value/value';
        return patch.op === 'replace' && patch.path.match(pathRegex) && patch.path.match(pathRegex).length === 4;
    }

    // private _isBoxArtboardAddRemovePatch(patch:IJsonPatch):boolean
    // {
    //     const pathRegex = '/surface/pages/(.*)/artboard/boxes/(\\d+)$';
    //     return (patch.op === 'add' || patch.op === 'remove') && patch.path.match(pathRegex) && patch.path.match(pathRegex).length === 3;
    // }

    // private _isBoxChildrenAddRemovePatch(patch:IJsonPatch):boolean
    // {
    //     const pathRegex = '/surface/pages/(.*)/artboard/allBoxes/(.*)/children/(\\d+)$';
    //     return (patch.op === 'add' || patch.op === 'remove') && patch.path.match(pathRegex) && patch.path.match(pathRegex).length === 4;
    // }

    // private _isBoxChangeParentPatch(patch:IJsonPatch):boolean
    // {
    //     const pathRegex = '/surface/pages/(.*)/artboard/allBoxes/(.*)/parent';
    //     return patch.op === 'replace' && patch.path.match(pathRegex) && patch.path.match(pathRegex).length === 3;
    // }

    private _isTextValuePatch(patch:IJsonPatch):boolean
    {
        const pathRegex = '/surface/pages/(.*)/artboard/allBoxes/(.*)/componentDesc/_properties/model/_value/text/value';
        return patch.op === 'replace' && patch.path.match(pathRegex) && patch.path.match(pathRegex).length === 3;
    }

    private _isSlotFormulaPatch(patch:IJsonPatch):boolean
    {
        const pathRegex = '^/spreadsheets/workbooks/(.*)/runtime/sheets/(.*)/slots/(.*)/formula$';
        return patch.op === 'replace' && patch.path.match(pathRegex) && patch.path.match(pathRegex).length === 4;
    }

    private _isSlotFormulaValuePatch(patch:IJsonPatch):boolean
    {
        const pathRegex = '^/spreadsheets/workbooks/(.*)/runtime/sheets/(.*)/slots/(.*)/formula/value$';
        return patch.op === 'replace' && patch.path.match(pathRegex) && patch.path.match(pathRegex).length === 4;
    }
}
